/**
 * Tenant Context
 *
 * Provides tenant configuration and branding throughout the application.
 * Detects tenant from:
 * 1. Custom domain (e.g., intake.elitepain.com)
 * 2. Subdomain (e.g., elitepain.empowercare.com)
 * 3. Query parameter (?tenant=elitepain)
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { environment } from '../env/environment';
import logger from '../utils/logger';

const decodeHtmlEntities = (str) => {
  if (!str || typeof str !== 'string') return str;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

// Default branding (fallback)
const DEFAULT_BRANDING = {
  id: null,
  name: 'Empower Care',
  brandName: 'Empower Care',
  slug: 'default',
  logoUrl: null,
  faviconUrl: null,
  primaryColor: '#06AB8B',
  secondaryColor: '#049572',
  features: {
    pain_diagram: true,
    followup_enabled: true,
    pdf_generation: true,
    google_drive_upload: true
  }
};

// Create context
const TenantContext = createContext({
  tenant: DEFAULT_BRANDING,
  loading: true,
  error: null,
  refetch: () => {}
});

/**
 * Clear tenant data from localStorage and window
 */
const clearTenantData = () => {
  localStorage.removeItem('dev_tenant_id');
  localStorage.removeItem('dev_tenant');
  window.__TENANT_ID__ = null;
};

/**
 * Extract tenant identifier from current URL
 */
const extractTenantFromUrl = () => {
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);

  // 1. Check query parameter first (?tenant=elitepain)
  const queryTenant = searchParams.get('tenant');
  if (queryTenant) {
    return { type: 'slug', value: queryTenant };
  }

  // 2. For localhost WITHOUT query param, check path or clear
  if (hostname === 'localhost' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    const currentPath = window.location.pathname;

    // Only persist tenant if navigating within app (not on root)
    if (currentPath !== '/' && currentPath !== '') {
      const devTenant = localStorage.getItem('dev_tenant');
      if (devTenant) {
        return { type: 'slug', value: devTenant };
      }
    } else {
      // On root page without tenant param - clear stored tenant
      clearTenantData();
    }
    return null;
  }

  // 3. For known deployment domains — always use domain lookup
  const knownDomains = ['epainhealth.com', 'sevalla.app', 'kinsta.page', 'kinsta.app', 'on-aptible.com'];
  const isKnownDomain = knownDomains.some(d => hostname.endsWith(d));
  if (isKnownDomain) {
    return { type: 'domain', value: hostname };
  }

  // 4. Check if it's a custom domain (third-party like orthobiogen.com)
  const isCustomDomain = !hostname.includes('empowercare') &&
                         !hostname.includes('kinsta') &&
                         !hostname.includes('localhost');

  if (isCustomDomain) {
    return { type: 'domain', value: hostname };
  }

  // 5. Extract subdomain for legacy kinsta/empowercare domains
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    const subdomain = parts[0];
    if (!['www', 'api', 'admin', 'app', 'intake', 'newintake', 'newfollowup', 'superadmin', 'intakeadmin'].includes(subdomain)) {
      return { type: 'slug', value: subdomain };
    }
  }

  return null;
};

/**
 * Fetch tenant configuration from API
 */
// Only pending public configuration requests are shared, never patient data.
// Entries are removed on settlement so explicit refreshes always reach the API.
const pendingTenantRequests = new Map();
const fetchTenantConfig = async (tenantInfo, useBootstrap = false) => {
  try {
    let url;

    if (tenantInfo.type === 'slug') {
      url = `${environment.baseUrl}/api/tenants/slug/${encodeURIComponent(tenantInfo.value)}`;
    } else if (tenantInfo.type === 'domain') {
      // For custom domains, use the domain lookup endpoint
      url = `${environment.baseUrl}/api/tenants/domain/${encodeURIComponent(tenantInfo.value)}`;
    } else {
      return null;
    }

    // Old servers ignore this additive selector and return the full response.
    url += '?view=intake';

    const bootstrap = window.__TENANT_REQUEST__;
    if (useBootstrap && bootstrap?.url === url) {
      try {
        const prefetched = await bootstrap.promise;
        if (prefetched) return prefetched;
      } catch (_) {
        // The early request is bounded by a timeout; fall back to a fresh attempt.
      }
    }

    if (useBootstrap && pendingTenantRequests.has(url)) {
      return await pendingTenantRequests.get(url);
    }

    logger.log('[TenantContext] Fetching tenant from:', url);

    const request = (async () => {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) {
        if (response.status === 404) {
          logger.warn('[TenantContext] Tenant not found:', tenantInfo.value);
          return null;
        }
        throw new Error(`Failed to fetch tenant: ${response.status}`);
      }

      const data = await response.json();
      logger.log('[TenantContext] API returned tenant:', data.tenant?.id, data.tenant?.name);
      return data.tenant;
    })();
    pendingTenantRequests.set(url, request);
    try {
      return await request;
    } finally {
      if (pendingTenantRequests.get(url) === request) pendingTenantRequests.delete(url);
    }
  } catch (error) {
    logger.error('[TenantContext] Error fetching tenant config:', error);
    return null;
  }
};

/**
 * Transform API response to usable tenant object
 * Uses accountBranding.client for client-specific branding
 */
export const transformTenant = (apiTenant) => {
  if (!apiTenant) return null;

  // Get client-specific branding from accountBranding (if exists)
  const clientBranding = apiTenant.account_branding?.client || {};

  // Debug logging for branding
  logger.log('[TenantContext] Raw account_branding:', apiTenant.account_branding);
  logger.log('[TenantContext] Client branding extracted:', clientBranding);
  logger.log('[TenantContext] Raw primary_color:', apiTenant.primary_color);
  logger.log('[TenantContext] Using primaryColor:', clientBranding.primaryColor || apiTenant.primary_color || DEFAULT_BRANDING.primaryColor);
  logger.log('[TenantContext] Using secondaryColor:', clientBranding.secondaryColor || apiTenant.secondary_color || DEFAULT_BRANDING.secondaryColor);

  return {
    id: apiTenant.id,
    name: apiTenant.name,
    brandName: decodeHtmlEntities(apiTenant.brand_name || apiTenant.name),
    slug: apiTenant.slug,
    logoUrl: apiTenant.logo_url,
    logoDisplayUrl: apiTenant.logo_display_url,
    logoDimensions: apiTenant.logo_dimensions,
    faviconUrl: apiTenant.favicon_display_url || apiTenant.favicon_url,
    // Use client-specific colors if available, otherwise fallback to global
    primaryColor: clientBranding.primaryColor || apiTenant.primary_color || DEFAULT_BRANDING.primaryColor,
    secondaryColor: clientBranding.secondaryColor || apiTenant.secondary_color || DEFAULT_BRANDING.secondaryColor,
    buttonHoverColor: clientBranding.buttonHoverColor || apiTenant.button_hover_color || null,
    buttonBoxShadow: clientBranding.buttonBoxShadow || apiTenant.button_box_shadow || null,
    customDomain: apiTenant.custom_domain,
    features: apiTenant.features || DEFAULT_BRANDING.features,
    settings: apiTenant.settings || {},
    thankYouConfig: apiTenant.thank_you_config || {}
  };
};

/**
 * Apply tenant branding to document
 */
export const applyBrandingToDocument = (tenant) => {
  logger.log('[TenantContext] Applying branding to document:', {
    primaryColor: tenant.primaryColor,
    secondaryColor: tenant.secondaryColor,
    buttonHoverColor: tenant.buttonHoverColor,
    buttonBoxShadow: tenant.buttonBoxShadow
  });

  // Set CSS variables
  document.documentElement.style.setProperty('--color-primary', tenant.primaryColor);
  document.documentElement.style.setProperty('--color-secondary', tenant.secondaryColor);
  document.documentElement.style.setProperty('--color-primary-light', tenant.primaryColor + '20');
  document.documentElement.style.setProperty('--color-primary-dark', tenant.secondaryColor);

  // Button hover color - fallback to secondary if not set
  const hoverColor = tenant.buttonHoverColor || tenant.secondaryColor;
  document.documentElement.style.setProperty('--color-primary-hover', hoverColor);
  document.documentElement.style.setProperty('--color-primary-dark', tenant.secondaryColor);
  document.documentElement.style.setProperty('--color-button-hover', hoverColor);

  // Button box shadow
  if (tenant.buttonBoxShadow) {
    document.documentElement.style.setProperty('--button-box-shadow', tenant.buttonBoxShadow);
  } else {
    document.documentElement.style.setProperty('--button-box-shadow', 'none');
  }

  // Set tenant ID on window for API access (ALWAYS set before checking)
  if (tenant.id) {
    window.__TENANT_ID__ = tenant.id;
    localStorage.setItem('dev_tenant_id', String(tenant.id));
    localStorage.setItem('dev_tenant', tenant.slug);
    logger.log('[TenantContext] Set tenant ID:', tenant.id, 'slug:', tenant.slug);
  } else {
    // Clear tenant data when using default branding
    clearTenantData();
    logger.log('[TenantContext] Using default branding (no tenant)');
  }

  // Set favicon - remove all existing and add new one
  if (tenant.faviconUrl && !tenant.faviconUrl.includes('/uploads/')) {
    // Skip cache-busting for base64 data URLs (stored in DB, always fresh)
    const faviconHref = tenant.faviconUrl.startsWith('data:') || /\/api\/tenants\/\d+\/favicon\?v=[a-f0-9]{16}(?:&w=64)?$/.test(tenant.faviconUrl)
      ? tenant.faviconUrl
      : tenant.faviconUrl + (tenant.faviconUrl.includes('?') ? '&' : '?') + '_t=' + Date.now();

    // Remove ALL existing favicon links
    const existingIcons = document.querySelectorAll("link[rel*='icon']");
    existingIcons.forEach(icon => icon.remove());

    // Create new favicon link — detect mime type from data URL or default to x-icon
    const faviconType = tenant.faviconUrl.startsWith('data:')
      ? tenant.faviconUrl.split(';')[0].split(':')[1]
      : 'image/x-icon';
    const faviconLink = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = faviconType;
    faviconLink.href = faviconHref;
    document.head.appendChild(faviconLink);

    // Also add shortcut icon for older browsers
    const shortcutLink = document.createElement('link');
    shortcutLink.rel = 'shortcut icon';
    shortcutLink.href = faviconHref;
    document.head.appendChild(shortcutLink);
  }

  // Set page title
  if (tenant.brandName) {
    document.title = `${tenant.brandName} - Patient Intake Form`;
  }

  // Update meta tags for SEO
  const updateMetaTag = (selector, content) => {
    const meta = document.querySelector(selector);
    if (meta) meta.content = content;
  };

  if (tenant.brandName) {
    updateMetaTag('meta[property="og:title"]', tenant.brandName);
    updateMetaTag('meta[name="twitter:title"]', tenant.brandName);
    updateMetaTag('meta[name="apple-mobile-web-app-title"]', tenant.brandName);
  }

  if (tenant.logoUrl) {
    updateMetaTag('meta[property="og:image"]', tenant.logoUrl);
    updateMetaTag('meta[name="twitter:image"]', tenant.logoUrl);
  }
};

/**
 * Tenant Provider Component
 */
export const TenantProvider = ({ children }) => {
  const [tenant, setTenant] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadSequence = useRef(0);

  const loadTenant = useCallback(async (useBootstrap = false) => {
    const sequence = ++loadSequence.current;
    if (!useBootstrap) {
      delete window.__TENANT_REQUEST__;
      delete window.__TENANT_DATA__;
    }
    try {
      setLoading(true);
      setError(null);

      const tenantInfo = extractTenantFromUrl();
      logger.log('[TenantContext] Extracted tenant info:', tenantInfo);

      if (!tenantInfo) {
        // No tenant detected, clear stored data and use default
        logger.log('[TenantContext] No tenant detected, using default');
        clearTenantData();
        setTenant(DEFAULT_BRANDING);
        applyBrandingToDocument(DEFAULT_BRANDING);
        return;
      }

      const apiTenant = await fetchTenantConfig(tenantInfo, useBootstrap);
      if (sequence !== loadSequence.current) return;
      // Only the matching, current request may set tenant identity/branding.
      delete window.__TENANT_DATA__;

      if (apiTenant) {
        const transformedTenant = transformTenant(apiTenant);
        logger.log('[TenantContext] Loaded tenant:', transformedTenant.id, transformedTenant.name);

        // IMPORTANT: Set window.__TENANT_ID__ BEFORE setting state
        // This ensures it's available immediately for any useEffect that runs
        if (transformedTenant.id) {
          window.__TENANT_ID__ = transformedTenant.id;
          localStorage.setItem('dev_tenant_id', String(transformedTenant.id));
          localStorage.setItem('dev_tenant', transformedTenant.slug);
        }

        setTenant(transformedTenant);
        applyBrandingToDocument(transformedTenant);
      } else {
        // Tenant not found, clear stored data and use default
        logger.log('[TenantContext] Tenant not found, using default');
        clearTenantData();
        setTenant(DEFAULT_BRANDING);
        applyBrandingToDocument(DEFAULT_BRANDING);
      }
    } catch (err) {
      if (sequence !== loadSequence.current) return;
      logger.error('[TenantContext] Error loading tenant:', err);
      setError(err.message);
      setTenant(DEFAULT_BRANDING);
      applyBrandingToDocument(DEFAULT_BRANDING);
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTenant(true);
    return () => { loadSequence.current += 1; };
  }, [loadTenant]);

  const value = {
    tenant,
    loading,
    error,
    refetch: loadTenant,
    // Helper getters
    isFeatureEnabled: (feature) => tenant.features?.[feature] ?? true,
    getLogo: () => tenant.logoUrl,
    getPrimaryColor: () => tenant.primaryColor,
    getSecondaryColor: () => tenant.secondaryColor,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

/**
 * Hook to use tenant context
 */
export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

/**
 * HOC to inject tenant props
 */
export const withTenant = (Component) => {
  return function WithTenantComponent(props) {
    const tenantContext = useTenant();
    return <Component {...props} tenant={tenantContext} />;
  };
};

export default TenantContext;
